import { Contract, Signer, Provider, InterfaceAbi } from 'ethers';
import Logger from '@blink/components/src/utils/logger';

/**
 * Creates a wrapped contract instance where all methods are wrapped with try-catch
 * to handle errors gracefully. All nested methods of `execute` are also wrapped.
 *
 * @param address - Contract address
 * @param abi - Contract ABI
 * @param signerOrProvider - Signer or provider for interacting with the contract
 * @returns A contract instance with wrapped methods
 */
export function getSafeContract<T extends Contract>(
    address: string,
    abi: InterfaceAbi,
    signerOrProvider: Signer | Provider | null,
): T {
    const contract = new Contract(address, abi, signerOrProvider) as T;

    // eslint-disable-next-line @typescript-eslint/ban-types
    function wrapFunctionWithTryCatch(func: Function, methodName: string): Function {
        return new Proxy(func, {
            apply(targetFunc, thisArg, argumentsList) {
                try {
                    return Reflect.apply(targetFunc, thisArg, argumentsList);
                } catch (error: unknown) {
                    Logger.error(`Error calling contract method ${methodName}:`, { error });

                    const customError = new Error(
                        `Contract method ${methodName} failed: ${
                            error instanceof Error ? error.message : 'Unknown error'
                        }`,
                    ) as any;

                    customError.code = 'CONTRACT_METHOD_ERROR';

                    throw customError;
                }
            },
            get(targetFunc, nestedProp) {
                const nestedMethod = Reflect.get(targetFunc, nestedProp);

                if (typeof nestedMethod === 'function') {
                    return wrapFunctionWithTryCatch(
                        nestedMethod,
                        `${methodName}.${String(nestedProp)}`,
                    );
                }

                return nestedMethod;
            },
        });
    }

    return new Proxy(contract, {
        get(target, prop: string | symbol, receiver) {
            const originalMethod = Reflect.get(target, prop, receiver);

            if (prop === 'execute') {
                return originalMethod;
            }

            if (typeof originalMethod === 'function') {
                return wrapFunctionWithTryCatch(originalMethod, String(prop));
            }

            return originalMethod;
        },
    });
}
